import { Button, Divider } from '@design-system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';

import { useProductCheckoutLink } from '../../../../features/checkout/hooks/useProductCheckoutLink';
import { PreAuthDrawButton } from '../../../../features/draws/components/PreAuthDrawButton';
import { useDrawButtonLabels } from '../../../../features/draws/hooks/useDrawButtonLabels';
import { buildDrawFlowConfig } from '../../../../features/draws/utils/useDrawFlow';
import { useDrawStage } from '../../../../hooks/useDrawStage';
import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useUrlHasParam } from '../../../../hooks/useUrlHasParam';
import { useFlow } from '../../../../providers';
import { useDraws } from '../../../../providers/DrawProvider';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { isInNextSixDays } from '../../../../utils/product';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import ProductDate from '../../ProductDate';
import { DrawExplainer } from './DrawExplainer';
import { DrawFlowDialogHosts } from './DrawFlowDialogHosts';
import { MultiProductDrawFlow } from './MultiProductDrawFlow';
import { PreOrderHeader } from './PreOrderHeader';

export const DrawFlow = () => {
  const { t } = useTranslation(['product', 'authentication']);
  const [showDialog, setShowDialog] = useState(false);
  const { startFlow } = useFlow();

  const { isAccountInDraw, linkedDraws, mainDraw } = useDraws();

  const stage = useDrawStage(mainDraw?.draw);
  const isPreOrder = useIsPreOrder(mainDraw?.draw);

  const checkoutLink = useProductCheckoutLink();
  const labels = useDrawButtonLabels('mainCta', isPreOrder);

  const hasCheckoutParam = useUrlHasParam('checkout_id');
  const { resetController } = usePreAuthDrawCtx();

  const isFramedByDefaultFeatureEnabled = useFeatureIsOn('framed-by-default');

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'draw',
    });
  }, [emitter]);

  const handleFlowClose = useCallback(() => {
    resetController();
  }, [resetController]);

  const drawButtons = useMemo(() => {
    if (!mainDraw) {
      return null;
    }

    if (checkoutLink) {
      return (
        <Button
          as="a"
          fullWidth
          href={checkoutLink}
          label={
            linkedDraws.length > 0
              ? t('drawers.review.draw.multiProduct.drawButtonTitle')
              : t(labels.normal)
          }
          onClick={() => {
            emitter('draw entry', 'clicked', {
              'product title': mainDraw.product.title,
              'artist name': mainDraw.product.artists[0]?.name ?? '',
              'product price': mainDraw.product.price,
            });
          }}
          size="lg"
          variant="primary"
        />
      );
    }

    if (isAccountInDraw(mainDraw.draw.id)) {
      return (
        <Button
          disabled
          fullWidth
          icon="ic_check"
          label={t('subscriptionCta.draw.buttonSuccessTitle')}
          size="lg"
          variant="positive"
        />
      );
    }

    if (linkedDraws.length === 0) {
      const flowConfig = buildDrawFlowConfig(
        t,
        mainDraw,
        isPreOrder,
        isFramedByDefaultFeatureEnabled,
      );
      return (
        <PreAuthDrawButton
          buttonLocation="mainCta"
          drawWithFrames={mainDraw}
          hasCheckoutParam={hasCheckoutParam}
          startDrawFlow={() => {
            startFlow({
              ...flowConfig,
              onFlowComplete: handleFlowClose,
            });
          }}
        />
      );
    }

    return (
      <MultiProductDrawFlow
        linkedDraws={linkedDraws}
        mainDraw={mainDraw}
        t={t}
      />
    );
  }, [
    mainDraw,
    checkoutLink,
    emitter,
    labels.normal,
    isAccountInDraw,
    linkedDraws,
    t,
    isPreOrder,
    isFramedByDefaultFeatureEnabled,
    hasCheckoutParam,
    startFlow,
    handleFlowClose,
  ]);

  if (!mainDraw) {
    return null;
  }

  return (
    <>
      {stage === 'DRAW_PRE_ORDER_CLOSED' && (
        <>
          <PreOrderHeader active={false} title={t('preOrder.isClosed.title')} />
          <Divider className="my-6" variant="dashed" />
        </>
      )}
      <div className="mb-6 flex w-full flex-col space-y-3 text-center">
        <div>
          <p className="w-full font-bold">
            {isInNextSixDays(new Date(mainDraw.draw.closeAt))
              ? t('subscriptionDate.draw.countdown')
              : t('subscriptionDate.draw.date')}
          </p>
          <p className="w-full">
            <ProductDate date={new Date(mainDraw.draw.closeAt)} />
          </p>
        </div>
        {mainDraw.draw.allocation && (
          <p className="w-full text-neutral-4">
            {t(
              stage === 'DRAW_PRE_ORDER_CLOSED'
                ? 'subscriptionCta.preOrder.titleAllocation'
                : 'subscriptionCta.draw.titleAllocation',
              { amount: mainDraw.draw.allocation },
            )}
          </p>
        )}
      </div>
      {drawButtons}
      <button
        className="hyperlink mt-3"
        onClick={() => {
          setShowDialog(true);
          handleHowToClick();
        }}
        type="button"
      >
        {t('subscriptionCta.draw.learnMoreDialog.title')}
      </button>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.draw.learnMoreDialog.title')}
      >
        <DrawExplainer
          cryptoComEnabled={mainDraw.draw.cryptoComEnabled && !isPreOrder}
        />
        {showDialog && drawButtons}
      </SimpleDialog>
      <DrawFlowDialogHosts />
    </>
  );
};
